import { useEffect, useMemo, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '@/store/store';
import { fetchPredictionsRequested } from '@/store/slices/propensitySlice';
import {
  Box,
  Typography,
  Chip,
  LinearProgress,
  Alert,
  Stack,
  TextField,
  IconButton,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Pagination,
  Divider
} from '@mui/material';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import InsightsIcon from '@mui/icons-material/Insights';
import { alpha } from '@mui/material/styles';
import { setPropensitySearch, setPropensityPagination } from '@/store/slices/propensitySlice';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import PropensityManagerDrillDown from '@/components/propensity/PropensityManagerDrillDown';

function ProbabilityCell({ value }: { value: number }) {
  const clamped = Math.max(0, Math.min(1, value));
  return (
    <Box minWidth={160}>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={0.5}>
        <Typography variant="caption" color="text.secondary">{Math.round(clamped * 100)}%</Typography>
      </Box>
      <LinearProgress
        variant="determinate"
        value={clamped * 100}
        sx={{
          height: 8,
          borderRadius: 6,
          bgcolor: theme => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)',
          '& .MuiLinearProgress-bar': theme => ({
            background: `linear-gradient(90deg, ${theme.palette.success.main}, ${theme.palette.success.light})`
          })
        }}
        aria-label={`Renewal probability ${Math.round(clamped * 100)} percent`}
      />
    </Box>
  );
}

const FactorsSummary = ({
  positives,
  negatives
}: {
  positives: { display_name: string; formatted_impact: string }[];
  negatives: { display_name: string; formatted_impact: string }[];
}) => {
  const topPos = (positives || []).slice(0, 2);
  const topNeg = (negatives || []).slice(0, 2);
  return (
    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
      {topPos.map((f, i) => (
        <Tooltip key={`p-${i}`} title={f.formatted_impact}>
          <Chip size="small" color="success" variant="outlined" label={f.display_name} />
        </Tooltip>
      ))}
      {topNeg.map((f, i) => (
        <Tooltip key={`n-${i}`} title={f.formatted_impact}>
          <Chip size="small" color="error" variant="outlined" label={f.display_name} />
        </Tooltip>
      ))}
      {(positives?.length || 0) + (negatives?.length || 0) > 4 && (
        <Tooltip title="View all factors">
          <InfoOutlinedIcon color="primary" fontSize="small" />
        </Tooltip>
      )}
    </Stack>
  );
};

export default function PropensityPredictions() {
  const dispatch = useDispatch();
  const { items, total, limit, offset, isLoading, error, search } = useSelector((s: RootState) => s.propensity);
  const [localSearch, setLocalSearch] = useState(search || '');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogTitle, setDialogTitle] = useState<string>('');
  const [dialogPositives, setDialogPositives] = useState<{ display_name: string; impact_percentage: number; formatted_impact: string }[]>([]);
  const [dialogNegatives, setDialogNegatives] = useState<{ display_name: string; impact_percentage: number; formatted_impact: string }[]>([]);
  const [tab, setTab] = useState<number>(0);

  const page = useMemo(() => Math.floor((offset || 0) / (limit || 1)) + 1, [offset, limit]);
  const pageCount = useMemo(() => Math.max(1, Math.ceil((total || 0) / (limit || 1))), [total, limit]);

  useEffect(() => {
    dispatch(fetchPredictionsRequested({ limit, offset, search }));
  }, [dispatch]);

  const onRefresh = useCallback(() => {
    dispatch(fetchPredictionsRequested({ limit, offset, search }));
  }, [dispatch, limit, offset, search]);

  const onPageChange = useCallback((_: React.ChangeEvent<unknown>, newPage: number) => {
    const newOffset = (newPage - 1) * (limit || 20);
    dispatch(fetchPredictionsRequested({ limit, offset: newOffset, search }));
  }, [dispatch, limit, search]);

  const onSearch = useCallback(() => {
    const trimmed = localSearch.trim();
    // keep Redux state in sync so pagination and future requests use the same search
    dispatch(setPropensitySearch(trimmed));
    dispatch(setPropensityPagination({ offset: 0 }));
    dispatch(fetchPredictionsRequested({ limit, offset: 0, search: trimmed }));
  }, [dispatch, localSearch, limit]);

  const openFactorsDialog = useCallback((title: string, positives: any[], negatives: any[]) => {
    setDialogTitle(title);
    setDialogPositives(positives || []);
    setDialogNegatives(negatives || []);
    setDialogOpen(true);
  }, []);

  const closeDialog = useCallback(() => setDialogOpen(false), []);

  return (
    <Box>
      <Box sx={{ mb: 2 }}>
        <Typography 
          variant="h4" 
          component="h1" 
          sx={{ 
            fontWeight: 600,
            color: '#003087',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            mb: 1
          }}
        >
          <InsightsIcon sx={{ color: '#E31837' }} />
          Renewal Propensity
        </Typography>
        <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 2 }}>
          Explore customer renewal likelihood and driving factors
        </Typography>
      </Box>

      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} aria-label="propensity view tabs">
          <Tab label="Policy Level" />
          <Tab label="Manager Breakdown" />
        </Tabs>
        {tab === 0 && (
          <Stack direction="row" spacing={1} alignItems="center">
            <TextField
              size="small"
              placeholder="Search by Policy ID or Customer ID"
              inputProps={{ 'aria-label': 'Search by Policy ID or Customer ID' }}
              value={localSearch}
              onChange={(e) => setLocalSearch(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') onSearch();
              }}
            />
            <Tooltip title="Search">
              <IconButton color="primary" aria-label="search" onClick={onSearch}>
                <SearchIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Refresh">
              <IconButton color="primary" aria-label="refresh" onClick={onRefresh}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Stack>
        )}
      </Stack>

      {isLoading && (
        <Box sx={{ my: 1 }}>
          <LinearProgress aria-label="Loading predictions" />
        </Box>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
      )}

      {tab === 0 && (
      <TableContainer component={Paper} sx={{ borderRadius: 2, overflow: 'auto' }}>
        <Table size="small" stickyHeader aria-label="propensity predictions table">
          <TableHead>
            <TableRow>
              <TableCell>Policy</TableCell>
              <TableCell>Agent</TableCell>
              <TableCell>Channel</TableCell>
              <TableCell>Sub Channel</TableCell>
              <TableCell>Region</TableCell>
              <TableCell>Renewal Probability</TableCell>
              <TableCell>Prediction</TableCell>
              <TableCell>Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map(row => {
              const prob = Number(row.renewal_probability || 0);
              const isRenew = row.predicted_renewal === 1;
              return (
                <TableRow key={row.id} hover>
                  <TableCell>
                    <Stack spacing={0.5}>
                      <Typography variant="body2" fontWeight={700}>{row.policy_id}</Typography>
                    </Stack>
                  </TableCell>
                  <TableCell>
                    {row.agent_code && (
                      <Chip 
                        size="small" 
                        label={row.agent_code}
                        variant="outlined"
                        sx={{ fontWeight: 600 }}
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    {row.channel && <Chip size="small" label={row.channel} />}
                  </TableCell>
                  <TableCell>
                    {row.sub_channel && <Chip size="small" label={row.sub_channel} />}
                  </TableCell>
                  <TableCell>
                    {row.chola_state && <Chip size="small" variant="outlined" label={row.chola_state} />}
                  </TableCell>
                  <TableCell>
                    <ProbabilityCell value={isNaN(prob) ? 0 : prob} />
                  </TableCell>
                  <TableCell>
                    <Chip size="small" color={isRenew ? 'success' : 'warning'} label={isRenew ? 'Likely Renewal' : 'At Risk'} />
                  </TableCell>
                  <TableCell>
                    {(row.top_positive_factors?.length || 0) + (row.top_negative_factors?.length || 0) > 0 && (
                      <Button 
                        size="small"
                        variant="outlined"
                        color="primary"
                        onClick={() => openFactorsDialog(`Factors for ${row.policy_id}`, row.top_positive_factors, row.top_negative_factors)}
                        sx={{ 
                          textTransform: 'none', 
                          fontWeight: 600,
                          borderRadius: 999,
                          px: 1.5,
                          backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.06),
                          borderColor: (theme) => alpha(theme.palette.primary.main, 0.3),
                          color: (theme) => theme.palette.primary.main,
                          boxShadow: 'none',
                          '&:hover': {
                            backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.1),
                            borderColor: (theme) => alpha(theme.palette.primary.main, 0.4),
                            boxShadow: 'none'
                          }
                        }}
                      >
                        View Factors
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      )}

      {tab === 0 && (
        <>
          <Divider sx={{ my: 2 }} />
          <Box display="flex" justifyContent="center" aria-label="pagination">
            <Pagination
              color="primary"
              page={page}
              count={pageCount}
              onChange={onPageChange}
              shape="rounded"
            />
          </Box>
        </>
      )}

      {tab === 1 && (
        <Box sx={{ mt: 2 }}>
          <PropensityManagerDrillDown />
        </Box>
      )}

      <Dialog open={dialogOpen} onClose={closeDialog} fullWidth maxWidth="md" aria-labelledby="factors-dialog-title">
        <DialogTitle id="factors-dialog-title">{dialogTitle}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={3}>
            <Box>
              <Typography variant="subtitle1" sx={{ mb: 1 }} color="success.main">Top Positive Factors</Typography>
              <Stack spacing={1}>
                {dialogPositives.map((f, i) => (
                  <Box key={`dp-${i}`}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Typography variant="body2" fontWeight={600}>{f.display_name}</Typography>
                      <Chip size="small" color="success" variant="outlined" label={f.formatted_impact} />
                    </Stack>
                    <LinearProgress
                      variant="determinate"
                      value={Math.min(100, Math.abs(f.impact_percentage))}
                      sx={{
                        height: 8,
                        borderRadius: 6,
                        bgcolor: theme => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)',
                        '& .MuiLinearProgress-bar': theme => ({ backgroundColor: theme.palette.success.main })
                      }}
                      aria-label={`${f.display_name} positive impact ${Math.round(Math.abs(f.impact_percentage))}%`}
                    />
                  </Box>
                ))}
              </Stack>
            </Box>

            <Box>
              <Typography variant="subtitle1" sx={{ mb: 1 }} color="error.main">Top Negative Factors</Typography>
              <Stack spacing={1}>
                {dialogNegatives.map((f, i) => (
                  <Box key={`dn-${i}`}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Typography variant="body2" fontWeight={600}>{f.display_name}</Typography>
                      <Chip size="small" color="error" variant="outlined" label={f.formatted_impact} />
                    </Stack>
                    <LinearProgress
                      variant="determinate"
                      value={Math.min(100, Math.abs(f.impact_percentage))}
                      sx={{
                        height: 8,
                        borderRadius: 6,
                        bgcolor: theme => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)',
                        '& .MuiLinearProgress-bar': theme => ({ backgroundColor: theme.palette.error.main })
                      }}
                      aria-label={`${f.display_name} negative impact ${Math.round(Math.abs(f.impact_percentage))}%`}
                    />
                  </Box>
                ))}
              </Stack>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog} autoFocus>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}


